import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { OpenAIBaseService } from "./open-ai-base.service";
import { environment } from "../../environments/environment";

/**
 * Service for analyzing resumes using OpenAI
 *
 * This service provides two methods for analyzing resumes:
 * 1. analyzeResume - Uses text-based analysis with gpt-4o-mini (primary, cost-effective approach)
 * 2. analyzeResumeFile - Uses vision-based analysis with gpt-4o for PDFs that can't be converted to text
 *
 * The normal flow is to first attempt text extraction from PDFs using other libraries,
 * then use analyzeResume with that text. Only if text extraction fails do we fall back
 * to the more expensive analyzeResumeFile method.
 */
@Injectable({
  providedIn: "root",
})
export class ResumeAnalyzerService extends OpenAIBaseService {
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB max file size

  constructor(protected override http: HttpClient) {
    super();
  }

  /**
   * Get a completion from OpenAI's API for resume analysis
   *
   * This is the PRIMARY approach that should be used whenever possible.
   * Used when text can be extracted from the PDF through standard libraries.
   * Much more cost-effective than the vision-based approach.
   */
  async analyzeResume(prompt: string): Promise<string> {
    const systemPrompt =
      "You are a helpful assistant that analyzes resumes and extracts structured information. You must respond with valid JSON only.";

    try {
      // Use JSON mode; higher max_tokens for full resume profile (work, skills, etc.)
      const content = await this.makeOpenAIChatRequest(
        prompt,
        systemPrompt,
        "gpt-4o-mini",
        0.7,
        true, // Enable JSON mode
        4096 // Resume profile JSON can be large
      );

      const parsedJson = this.parseJsonResponse(content);
      return JSON.stringify(parsedJson);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Analyze a resume file (PDF, image, etc.) using vision capabilities
   *
   * This is the FALLBACK approach when direct text extraction isn't possible.
   * Should only be used when regular text extraction methods fail.
   * Uses the more expensive gpt-4o model with vision capabilities.
   */
  public async analyzeResumeFile(file: File, prompt: string): Promise<string> {

    // No need to check for API key since we're using the secure proxy

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(
        `File size exceeds the maximum limit of ${
          this.MAX_FILE_SIZE / (1024 * 1024)
        }MB`
      );
    }

    // Validate supported file types
    const supportedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!supportedTypes.includes(file.type)) {
      throw new Error(
        `Unsupported file type: ${file.type}. Please upload a PDF, JPEG, or PNG file.`
      );
    }

    try {
      // This is the vision-based approach which requires the more powerful model
      // Convert file to base64
      const fileBase64 = await this.convertFileToBase64(file);
      const cleanBase64 = fileBase64.split(",")[1];

      // Prepare headers
      const headers = this.getHeadersForVision();

      // Using the full gpt-4o model which supports vision capabilities
      // This is more expensive but necessary for handling PDFs as images
      const model = "gpt-4o"; // Use the full model for better PDF handling

      // Always use image/png MIME type for vision API regardless of file type
      // This is required for OpenAI Vision API - it doesn't accept PDF MIME type
      const contentType = "image/png";

      // For image files or PDFs, use vision model
      const requestBody = {
        model: model,
        messages: [
          {
            role: "system",
            content:
              "You are a helpful assistant that analyzes resumes and extracts key information.",
          },
          {
            role: "user",
            content: [
              {
                type: "text",
                text: prompt,
              },
              {
                type: "image_url",
                image_url: {
                  // Always use image/png MIME type for vision API regardless of file type
                  url: `data:${contentType};base64,${cleanBase64}`,
                  detail: "high",
                },
              },
            ],
          },
        ],
        max_tokens: 4000,
        temperature: 0.3, // Lower temperature for more factual extraction
      };

      try {
        const response = await this.http
          .post<any>(
            "https://ykimklpimdbuwxceyngs.supabase.co/functions/v1/openai-proxy",
            requestBody,
            {
              headers,
            }
          )
          .toPromise();

        if (!response?.choices?.length) {
          throw new Error("No response choices returned from OpenAI");
        }

        return response.choices[0].message.content;
      } catch (apiError: any) {
        // Check if this is a model availability issue
        const errorMessage = apiError.error?.error?.message || "";

        if (apiError.status === 404) {
          throw new Error(
            "The AI model is currently unavailable. Please contact support or try again later."
          );
        } else if (apiError.status === 400) {
          // Common issues include file format or dimensions problems
          if (
            errorMessage.includes("unsupported image") ||
            errorMessage.includes("image format") ||
            errorMessage.includes("invalid_image_format")
          ) {
            // For PDF files specifically, we can give a more helpful error
            if (file.type === "application/pdf") {
              throw new Error(
                "This PDF cannot be processed by our vision model. Please try a different format or upload a simpler PDF."
              );
            } else {
              throw new Error(
                "The image format is not supported. Please use JPEG, PNG, GIF, or WEBP format."
              );
            }
          } else {
            // Generic error for other 400 issues
            throw new Error(
              errorMessage ||
                "Invalid request. The file may be corrupted or in an unsupported format."
            );
          }
        } else if (apiError.status === 401) {
          throw new Error(
            "API key is invalid or expired. Please check your OpenAI API key."
          );
        } else if (apiError.status === 429) {
          throw new Error("API rate limit exceeded. Please try again later.");
        } else {
          throw new Error(
            apiError.message || "Failed to process the resume with OpenAI API"
          );
        }
      }
    } catch (error: any) {
      throw new Error(
        error.message || "An error occurred processing the resume file"
      );
    }
  }

  /**
   * Convert a File object to base64 format
   */
  private async convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Get headers for the vision model API call
   */
  private getHeadersForVision(): HttpHeaders {
    // Use Supabase anon key for Edge Function authentication
    return new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${environment.supabaseKey}`,
    });
  }
}
